// Resolves the seats.io `maxSelectedObjects` for the buyer chart — how many seats one
// customer may select (and therefore hold). Two layers (issue #1087, follow-up to #1075):
//
//   1. If the producer set a per-event cap on the Seats tab (`event.maxSeatsPerUser`),
//      that wins — a single total number.
//   2. Otherwise fall back to the per-category caps derived from the tickets'
//      `countTicketsAllowPerPurchase` (the #1076 behaviour) — so removing the explicit
//      cap doesn't silently go *unlimited* and drop the producer's per-purchase limits.
//
// Mass-hold abuse is bounded server-side by the seats.io "max holds per token" setting.

export const DEFAULT_MAX_PER_CATEGORY = 10;

/**
 * Per-category caps from the seat tickets — `[{ category, quantity }]`, or a plain
 * fallback number when there are no seat categories (an empty array would make the whole
 * chart unselectable). seats.io matches `category` on the category KEY.
 * @param {Array} seatsTickets - tickets that have a `seatsCategoryKey`.
 */
export const computeMaxSelectedObjects = (seatsTickets, opts = {}) => {
    const fallback = opts.fallbackPerCategory ?? DEFAULT_MAX_PER_CATEGORY;

    const order = [];
    const definedLimits = {};
    for (const ticket of seatsTickets || []) {
        const key = ticket?.seatsCategoryKey;
        if (key === undefined || key === null || key === '') continue;
        if (!(key in definedLimits)) {
            definedLimits[key] = null;
            order.push(key);
        }
        const limit = Number(ticket.countTicketsAllowPerPurchase);
        if (Number.isFinite(limit) && limit >= 1) {
            definedLimits[key] = Math.max(definedLimits[key] ?? 0, limit);
        }
    }

    if (order.length === 0) return fallback;

    return order.map((category) => ({
        category: String(category),
        quantity: definedLimits[category] ?? fallback,
    }));
};

/**
 * The value to pass to seats.io `maxSelectedObjects`.
 * @param {object} event - the public event (may carry `maxSeatsPerUser`).
 * @param {Array} seatsTickets - the event's seat tickets (used for the fallback).
 * @returns {number|Array} the producer's per-event cap if set, else the per-category caps.
 */
export const resolveMaxSelectedObjects = (event, seatsTickets) => {
    const perUser = Number(event?.maxSeatsPerUser);
    if (Number.isFinite(perUser) && perUser >= 1) {
        return Math.floor(perUser);
    }
    return computeMaxSelectedObjects(seatsTickets);
};
